/**
 *  Cart
 *    Shared functions to handle all cart events. Used by both cart drawer
 *    and the cart template.
 *
 *  Version
 *    1.0.1  - 2022/08/26
 */

import { cartAdd, ON_CART_FINISHED, cartGetCurrent, cartUpdate } from "@process-creative/slate-theme-tools";
import {  CLASS_CART_PENDING, CLASS_HAS_ITEMS,
        CLASS_DRAWER_OPEN, CLASS_SCROLL_DISABLED, CLASS_ALLOW_SCROLL } from '../tools/constants/classes';
import { rebindContentDrawerEvents } from '../objects/content-drawer';
import { GiftThresholdManager } from '../objects/gift-threshold-manager';
import { Swiper } from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import type { CartItem } from '../types/cart';

const SELECTOR_CONTAINER = '[data-cart-container]';
const SELECTOR_TOGGLE = '[data-cart-drawer-toggle]';
const SELECTOR_SHOPIFY_SECTION = '#shopify-section-cart-contents';
const SELECTOR_UPSELL_ADD = '[data-upsell-add-btn]';
const SELECTOR_ITEM_REMOVE = '[data-remove-link]';
const SELECTOR_CART_COUNT = '[data-header-cart-counter]';

const SELECTOR_CART_RELATED_SECTION = '[data-cart-related-section]';
const SELECTOR_CART_RELATED_PRODUCTS = '[data-cart-related-products]';

const SELECTOR_SWIPER_CONTAINER   = '[data-swiper-container]';
const SELECTOR_SWIPER_NEXT        = '[data-swiper-next]';
const SELECTOR_SWIPER_PREV        = '[data-swiper-prev]';

const ATTR_ITEM_REMOVE = 'data-remove-link';
const ATTR_VARIANT = 'data-variant';

const body = document.body!;

document.querySelectorAll<HTMLDivElement>(SELECTOR_CONTAINER).forEach(container => {
  // Markers
  let contentsRendered = false;
  let cartOpen = false;

  // Elements
  let addUpsellBtns:NodeListOf<HTMLButtonElement>;
  let removeItemBtns:NodeListOf<HTMLButtonElement>;
  let toggleBtns:NodeListOf<HTMLButtonElement>;

  // Gift threshold manager
  let giftManager: GiftThresholdManager | null = null;

  // Event Listeners
  addEventListeners();
  initializeUpsellSection()
  window.addEventListener(ON_CART_FINISHED, onCartFinished);
  
  // Rebind content drawer events on initial load (for cart page)
  rebindContentDrawerEvents();

  // TO DO: For development purposes only, remove in production
  // renderCartContents();
  // openCart();

  function addEventListeners() {
    refreshElements();
    
    addUpsellBtns.forEach(btn => btn.addEventListener('click', onAddUpsell));
    removeItemBtns.forEach(btn => btn.addEventListener('click', onRemoveItem));
    toggleBtns.forEach(btn => btn.addEventListener('click', onCartToggle));
  }

  function removeEventListeners() {
    addUpsellBtns.forEach(btn => btn.removeEventListener('click', onAddUpsell));
    removeItemBtns.forEach(btn => btn.removeEventListener('click', onRemoveItem));
    toggleBtns.forEach(btn => btn.removeEventListener('click', onCartToggle));
  }

  function refreshElements() {
    addUpsellBtns = container.querySelectorAll(SELECTOR_UPSELL_ADD);
    removeItemBtns = container.querySelectorAll(SELECTOR_ITEM_REMOVE);
    toggleBtns = document.querySelectorAll(SELECTOR_TOGGLE);
  }

  function initializeSwiper() {
    const swiperContainerEl = container.querySelector(SELECTOR_SWIPER_CONTAINER) as HTMLElement;
    const nextEl = container.querySelector<HTMLElement>(SELECTOR_SWIPER_NEXT) || undefined;
    const prevEl = container.querySelector<HTMLElement>(SELECTOR_SWIPER_PREV) || undefined;
  
    if (!swiperContainerEl) return;

    const isCartPage = window.location.pathname === '/cart';
    const desktopSlidesPerView = isCartPage ? 1.5 : 2.5;
  
    new Swiper(swiperContainerEl, {
      modules: [Navigation, Pagination],
      spaceBetween: 4,
      navigation: {
        nextEl: nextEl,
        prevEl: prevEl,
      },
      breakpoints: {
        0: {
          slidesPerView: 1,
          spaceBetween: 4,
        },
        768: {
          slidesPerView: desktopSlidesPerView,
          spaceBetween: 4,
        }
      },
      loop: false,
      keyboard: true,
      grabCursor: true,
      touchMoveStopPropagation: false,
    });

    allowSwiperTouchEvents();
  }

  /**
   * Show upsell skeleton loader in the upsell section.
   */
  function showUpsellSkeleton(targetEl: Element) {
    targetEl.innerHTML = `
      <div class="c-cart-contents__skeleton-wrapper is-loading">
        <div class="c-cart-contents__skeleton">
          <div class="skeleton-item"></div>
          <div class="skeleton-item"></div>
        </div>
      </div>
    `;
  }

  function initializeUpsellSection() {
    const productUpsellSection = document.querySelector(SELECTOR_CART_RELATED_SECTION);
    if (!productUpsellSection) return;
  
    // Show skeleton while fetching recommendations
    showUpsellSkeleton(productUpsellSection);
    
    const url = productUpsellSection.getAttribute('data-url');
    if (!url) return;
  
    fetch(url)
      .then(response => response.text())
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;
        let recommendations = html.querySelector(SELECTOR_CART_RELATED_PRODUCTS);
        const hasContent = recommendations && recommendations.innerHTML.trim().length > 0;
        const urlObj = new URL(url!, window.location.origin);
        const currentIntent = urlObj.searchParams.get('intent');
  
        if (!hasContent && currentIntent === 'complementary') {
          urlObj.searchParams.set('intent', 'related');
          showUpsellSkeleton(productUpsellSection);
          fetch(urlObj.toString())
            .then(fallbackRes => fallbackRes.text())
            .then(fallbackText => {
              const fallbackHtml = document.createElement('div');
              fallbackHtml.innerHTML = fallbackText;
              const fallbackRecommendations = fallbackHtml.querySelector(SELECTOR_CART_RELATED_PRODUCTS);
              if (fallbackRecommendations) {
                productUpsellSection.innerHTML = '';
                productUpsellSection.appendChild(fallbackRecommendations);
                initializeSwiper();
                // Ensure swiper can receive touch events after initialization
                setTimeout(() => allowSwiperTouchEvents(), 100);
              }
            })
            .catch(e => console.error('Fallback fetch failed', e));
        } else if (hasContent) {
          productUpsellSection.innerHTML = '';
          productUpsellSection.appendChild(recommendations!);
          initializeSwiper();
          // Ensure swiper can receive touch events after initialization
          setTimeout(() => allowSwiperTouchEvents(), 100);
        }
      })
      .catch(e => console.error(e));
  }

  // Methods
  function onCartFinished() {
    // Refresh/render cart content
    renderCartContents();

    // Sync cartOpen state with actual DOM state
    const isCartOpenInDOM = container.classList.contains(CLASS_DRAWER_OPEN);
    if (isCartOpenInDOM !== cartOpen) {
      cartOpen = isCartOpenInDOM;
    }

    // Open cart drawer if it's not open and not on cart page
    // Check both the state variable and DOM state to ensure accuracy
    const shouldOpenCart = !cartOpen && !isCartOpenInDOM && !isCartPage();
    if (shouldOpenCart) {
      setTimeout(() => openCart(), 500);
    }

    const countElement = document.querySelector<HTMLElement>(SELECTOR_CART_COUNT);
    if (!countElement) return;

    // Update cart pill or cart count on header
    const count = cartGetCurrent()['item_count'] || 0;
    countElement.innerHTML = `(${count.toString()})`;

    if (count > 0) {
      countElement.classList.add(CLASS_HAS_ITEMS);
    } else {
      countElement.classList.remove(CLASS_HAS_ITEMS);
    }
  }

  function onCartToggle(e:Event) {
    e.preventDefault();
    if (!cartOpen) {
      if (!contentsRendered) renderCartContents();
      contentsRendered = true;
      openCart();
    } else {
      closeCart();
    }
  }

  function openCart() {
    container.classList.add(CLASS_DRAWER_OPEN);
    body.classList.add(CLASS_SCROLL_DISABLED);
    // Add data-lenis-prevent to prevent page scrolling when drawer is open
    container.setAttribute('data-lenis-prevent', '');
    // Enable scrolling on cart contents
    enableCartScrolling();
    // Allow swiper to receive touch events
    allowSwiperTouchEvents();
    cartOpen = true;
  }

  function closeCart() {
    container.classList.remove(CLASS_DRAWER_OPEN);
    body.classList.remove(CLASS_SCROLL_DISABLED);
    // Remove data-lenis-prevent
    container.removeAttribute('data-lenis-prevent');
    // Disable scrolling handlers
    disableCartScrolling();
    cartOpen = false;
  }

  // Manual scroll handlers to bypass Lenis
  let cartScrollHandlers: { element: HTMLElement; wheel: EventListener; touchStart: EventListener; touchMove: EventListener }[] = [];

  function enableCartScrolling() {
    const cartContents = container.querySelector<HTMLElement>('.c-cart-contents');
    if (!cartContents) return;

    // Remove lenis prevent attributes from cart contents
    cartContents.removeAttribute('data-lenis-prevent');
    cartContents.removeAttribute('data-lenis-prevent-wheel');
    cartContents.removeAttribute('data-lenis-prevent-touch');

    // Add class to allow scrolling (CSS fallback)
    cartContents.classList.add(CLASS_ALLOW_SCROLL);

    // Create manual scroll handlers
    const handleWheel = (e: Event) => {
      const wheelEvent = e as WheelEvent;
      const target = wheelEvent.currentTarget as HTMLElement;
      if (target) {
        wheelEvent.preventDefault();
        wheelEvent.stopPropagation();
        target.scrollTop += wheelEvent.deltaY;
      }
    };

    let touchStartY = 0;
    let touchStartX = 0;
    const handleTouchStart = (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (touchEvent.touches.length > 0) {
        touchStartY = touchEvent.touches[0].clientY;
        touchStartX = touchEvent.touches[0].clientX;
      }
    };

    const handleTouchMove = (e: Event) => {
      const touchEvent = e as TouchEvent;
      const target = touchEvent.currentTarget as HTMLElement;
      if (target && touchEvent.touches.length > 0) {
        const touchY = touchEvent.touches[0].clientY;
        const touchX = touchEvent.touches[0].clientX;
        const deltaY = touchStartY - touchY;
        const deltaX = touchStartX - touchX;
        
        // Check if touch is on a swiper container - if so, allow it to handle the event
        const swiperContainer = (touchEvent.target as HTMLElement)?.closest('[data-swiper-container]');
        if (swiperContainer) {
          // Allow swiper to handle horizontal swipes - don't prevent default
          return;
        }
        
        // Only prevent default for vertical scrolling
        // Check if movement is primarily vertical (more vertical than horizontal)
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          touchEvent.preventDefault();
          touchEvent.stopPropagation();
          target.scrollTop += deltaY;
          touchStartY = touchY;
        }
      }
    };

    // Add event listeners
    cartContents.addEventListener('wheel', handleWheel, { passive: false });
    cartContents.addEventListener('touchstart', handleTouchStart, { passive: false });
    cartContents.addEventListener('touchmove', handleTouchMove, { passive: false });

    // Store handlers for cleanup
    cartScrollHandlers.push({
      element: cartContents,
      wheel: handleWheel,
      touchStart: handleTouchStart,
      touchMove: handleTouchMove
    });

    allowSwiperTouchEvents();
  }

  function allowSwiperTouchEvents() {
    const swiperContainer = container.querySelector<HTMLElement>(SELECTOR_SWIPER_CONTAINER);
    if (!swiperContainer) {
      setTimeout(() => allowSwiperTouchEvents(), 50);
      return;
    }

    const cartRelated = swiperContainer.closest<HTMLElement>('.c-cart-related');
    const cartRelatedWrapper = swiperContainer.closest<HTMLElement>('.c-cart-contents__cart-related');

    swiperContainer.removeAttribute('data-lenis-prevent');
    swiperContainer.removeAttribute('data-lenis-prevent-wheel');
    swiperContainer.removeAttribute('data-lenis-prevent-touch');
    
    if (cartRelated) {
      cartRelated.removeAttribute('data-lenis-prevent');
      cartRelated.removeAttribute('data-lenis-prevent-wheel');
      cartRelated.removeAttribute('data-lenis-prevent-touch');
      cartRelated.classList.add(CLASS_ALLOW_SCROLL);
    }
    
    if (cartRelatedWrapper) {
      cartRelatedWrapper.removeAttribute('data-lenis-prevent');
      cartRelatedWrapper.removeAttribute('data-lenis-prevent-wheel');
      cartRelatedWrapper.removeAttribute('data-lenis-prevent-touch');
      cartRelatedWrapper.classList.add(CLASS_ALLOW_SCROLL);
    }

    swiperContainer.classList.add(CLASS_ALLOW_SCROLL);
    
    const swiperWrapper = swiperContainer.querySelector<HTMLElement>('.swiper-wrapper');
    if (swiperWrapper) {
      swiperWrapper.classList.add(CLASS_ALLOW_SCROLL);
    }
  }

  function disableCartScrolling() {
    // Remove all scroll handlers
    cartScrollHandlers.forEach(({ element, wheel, touchStart, touchMove }) => {
      element.removeEventListener('wheel', wheel);
      element.removeEventListener('touchstart', touchStart);
      element.removeEventListener('touchmove', touchMove);
      element.classList.remove(CLASS_ALLOW_SCROLL);
    });
    cartScrollHandlers = [];
  }

  function isCartPage() {
    return window.location.href.includes('/cart');
  }

  async function onAddUpsell(e:Event) {
    e.preventDefault();

    // Add upsell item to cart
    const target = e.target as HTMLButtonElement;
    const button = target.closest(SELECTOR_UPSELL_ADD);
    const variant = button?.getAttribute(ATTR_VARIANT);

    if (!variant) return;

    try {
      await cartAdd({
        items: [{ 
          id:parseInt(variant),
          quantity: 1
        }]
      });
    } catch(e) {
      console.error('Failed to add to cart.');
      return;
    }
  }

  async function onRemoveItem(e:Event) {
    e.preventDefault();

    // Remove item from cart
    const target = e.target as HTMLButtonElement;
    const button = target.closest(SELECTOR_ITEM_REMOVE);
    const line = button?.getAttribute(ATTR_ITEM_REMOVE);
    const itemsToRemove: { [key: string]: number } = {};

    if (line) {
      // Check if this is a gift item being removed manually
      const cart = cartGetCurrent();
      const cartItems = cart.items as unknown as CartItem[] | undefined;
      
      if (!cartItems || cartItems.length === 0) {
        console.warn('[Cart] No items in cart, cannot process removal');
        itemsToRemove[line] = 0;
      } else {
        const itemToRemove = cartItems.find(item => item.key === line);
        
        // Validate item exists and has required properties
        if (itemToRemove && itemToRemove.properties) {
          if (itemToRemove.properties._gift === 'true') {
            // Customer is manually removing a gift - mark it as dismissed
            const productIdStr = itemToRemove.properties._product_id;
            const thresholdStr = itemToRemove.properties._threshold;
            
            if (productIdStr && thresholdStr) {
              const productId = parseInt(productIdStr, 10);
              const threshold = parseFloat(thresholdStr);
              
              // Validate parsed numbers are valid
              if (!isNaN(productId) && !isNaN(threshold) && productId > 0 && threshold > 0) {
                GiftThresholdManager.markGiftDismissed(productId, threshold);
                console.log('[Cart] Gift marked as dismissed - productId:', productId, 'threshold:', threshold);
              } else {
                console.warn('[Cart] Invalid product ID or threshold values:', { productId, threshold });
              }
            }
          }
        }
        
        itemsToRemove[line] = 0;
      }
    }

    try {
      if (Object.keys(itemsToRemove).length) {
        await cartUpdate({ 
          updates: itemsToRemove
        });
      }
    } catch(e) {
      console.error('Failed to remove from cart.');
      return;
    }
  }

  /**
  *  renderCartContents
  *    Uses the Section Rendering API to render the contents of the cart
  *    drawer and print them in the container for the cart drawer
  */
  async function renderCartContents() {
    // Set global pending state
    body.classList.add(CLASS_CART_PENDING);

    removeEventListeners();

    // Render section
    const sectionHtml:string = await fetch(`/?sections=cart-contents`)
      .then(res => { return res.json()} )
      .then(res => res['cart-contents']);
    const html = document.createElement('div');
    
    html.innerHTML = sectionHtml;
    const sectionWrapper = html.querySelector<HTMLDivElement>(SELECTOR_SHOPIFY_SECTION);

    if (!sectionWrapper) {
      return console.warn('Invalid markup detected in the Cart Drawer section.');
    }

    container.innerHTML = String(sectionWrapper.outerHTML);

    // DOM changed, refresh listeners
    addEventListeners();
    initializeUpsellSection();
    
    rebindContentDrawerEvents();

    // Initialize or reinitialize gift manager
    if (giftManager) {
      giftManager.destroy();
    }
    giftManager = new GiftThresholdManager(container);

    // Check and update gifts after manager is initialized
    // Use timeout to ensure DOM is fully ready and prevent cart update loops
    setTimeout(() => {
      if (giftManager) {
        giftManager.checkAndUpdateGifts();
      }
    }, 150);

    // Re-enable scrolling on cart contents if drawer is open
    if (cartOpen) {
      enableCartScrolling();
      // Ensure swiper can receive touch events after cart is re-rendered
      setTimeout(() => allowSwiperTouchEvents(), 200);
    }

    // Remove global pending state
    body.classList.remove(CLASS_CART_PENDING);
  }
});